// Every rule the settings forms validate against, as pure functions: required
// fields, e-mail lists, file extensions, and waiving the password once OAuth
// tokens exist. Returns error codes, never user-facing messages.

import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';

// Validation results carry an error *code* rather than a message, so this module
// stays free of user-facing strings. `error` is:
//   undefined -> leave the current error untouched
//   null      -> clear the error
//   object    -> set { code, params }
const ok = (error) => ({ valid: true, error });
const fail = (error) => ({ valid: false, error });

export const ERROR_CODES = {
  BLANK: 'blank',
  INVALID_EMAIL: 'invalidEmail',
  INVALID_FORMAT: 'invalidFormat',
  INVALID_EXTENSION: 'invalidExtension',
  EXTENSION_MISMATCH: 'extensionMismatch',
};

export const ALLOWED_EXTENSIONS = ['xml', 'json', 'pdf'];

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

export const isValidEmail = (email) => EMAIL_REGEX.test(email);

// Headers are complete when every entry has both a key and a value; an absent or
// legacy non-array value counts as complete, since there is nothing to fill in.
export const isFieldFilled = (field) => {
  if (field.name.includes('headers')) {
    if (!Array.isArray(field.value) || isEmpty(field.value)) return true;

    return field.value.every(
      (entry) =>
        !isEmpty(trim(entry[0] || '')) && !isEmpty(trim(entry[1] || '')),
    );
  }

  return !isEmpty(trim(field.value || ''));
};

const PASSWORD_FIELD = /^(password)\d*$/i;

// Once OAuth tokens are populated the backend no longer needs the password, so it
// stops counting as required. Note that an integration with no token fields at all
// also satisfies this — `every` on an empty list is true.
export const tokensArePopulated = (fields) =>
  fields
    .filter((field) => field.name.includes('token'))
    .every((field) => !isEmpty(trim(field.value)));

export const fieldsToValidate = (fields, requiredNamesList) => {
  const required = fields.filter((field) =>
    requiredNamesList.includes(field.name),
  );

  return tokensArePopulated(fields)
    ? required.filter((field) => !PASSWORD_FIELD.test(field.name))
    : required;
};

export const isFormConfigured = (fields, requiredNamesList) =>
  fieldsToValidate(fields, requiredNamesList).every(isFieldFilled);

export const validateEmailList = (value) => {
  try {
    const allValid = JSON.parse(value).every(isValidEmail);

    return allValid
      ? ok(null)
      : fail({ code: ERROR_CODES.INVALID_EMAIL, params: {} });
  } catch (error) {
    return fail({ code: ERROR_CODES.INVALID_FORMAT, params: { error } });
  }
};

export const extensionOf = (value) => {
  const trimmed = trim(value);
  return trimmed.substring(trimmed.lastIndexOf('.') + 1).toLowerCase();
};

// `label` is the digit-stripped attribute name; `fileTypeFields` are the required
// fields whose label is 'file_type'.
export const validateExtension = ({ field, label, fileTypeFields }) => {
  if (label === 'recipients') return validateEmailList(field.value);

  if (label !== 'file_name' && label !== 'file_type') return ok(undefined);

  const ext = extensionOf(field.value);

  if (!ALLOWED_EXTENSIONS.includes(ext)) return fail(undefined);

  if (label === 'file_name' && fileTypeFields.length === 0)
    return ok(undefined);

  if (trim(fileTypeFields[0].value).toLowerCase() !== ext) {
    return fail({ code: ERROR_CODES.EXTENSION_MISMATCH, params: { ext } });
  }

  return ok(label === 'file_name' ? null : undefined);
};
